const DEFAULT_PROFILE_FIELDS = [
  'id',
  'email',
  'first_name',
  'last_name',
  'language',
  'avatar',
  'description',
  'location',
  'title',
  'position',
  'goals',
  'subscription_status',
  'subscription_plan',
  'subscription_period_end',
  'subscription_id',
  'linked_workspace.id',
  'linked_workspace.workspace_name',
  'linked_workspace.credits',
  'linked_workspace.widget_token',
  'linked_workspace.assistant_prompt'
];

function toFieldsParam(fields) {
  if (!fields) return undefined;
  if (Array.isArray(fields)) return fields.join(',');
  return fields;
}

function withQuery(url, query) {
  const u = new URL(url, window.location.origin);
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      u.searchParams.set(key, String(value));
    });
  }
  return u.toString();
}

async function readJsonSafe(response) {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

function getErrorMessage(json, fallback) {
  if (json?.errors?.[0]?.message) return json.errors[0].message;
  if (json?.message) return json.message;
  if (json?.error) return json.error;
  return fallback;
}

export function createAccountClient({
  directusBaseUrl,
  billingBaseUrl,
  getAccessToken,
  fetchImpl = fetch
}) {
  if (!directusBaseUrl) {
    throw new Error('directusBaseUrl is required');
  }

  const callJson = async ({ url, method = 'GET', body, token, fallbackError }) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetchImpl(url, {
      method,
      headers,
      ...(body ? { body: JSON.stringify(body) } : {})
    });

    const json = await readJsonSafe(response);

    if (!response.ok) {
      throw new Error(getErrorMessage(json, fallbackError || `Request failed (${response.status})`));
    }

    return json;
  };

  const getToken = () => (typeof getAccessToken === 'function' ? getAccessToken() : null);

  return {
    async getCurrentUser({ fields = DEFAULT_PROFILE_FIELDS } = {}) {
      const token = getToken();
      if (!token) throw new Error('No auth token');

      const url = withQuery(`${directusBaseUrl}/users/me`, {
        fields: toFieldsParam(fields)
      });
      const json = await callJson({
        url,
        token,
        fallbackError: 'Failed to load current user'
      });
      return json?.data || null;
    },

    async updateCurrentUser(payload) {
      const token = getToken();
      if (!token) throw new Error('No auth token');

      const json = await callJson({
        url: `${directusBaseUrl}/users/me`,
        method: 'PATCH',
        body: payload || {},
        token,
        fallbackError: 'Failed to update user profile'
      });
      return json?.data || null;
    },

    async getWorkspaceById(workspaceId, { fields = ['*'] } = {}) {
      const token = getToken();
      if (!token) throw new Error('No auth token');
      if (!workspaceId) throw new Error('workspaceId is required');

      const url = withQuery(`${directusBaseUrl}/items/workspace/${workspaceId}`, {
        fields: toFieldsParam(fields)
      });
      const json = await callJson({
        url,
        token,
        fallbackError: 'Failed to load workspace'
      });
      return json?.data || null;
    },

    async updateWorkspace(workspaceId, payload) {
      const token = getToken();
      if (!token) throw new Error('No auth token');
      if (!workspaceId) throw new Error('workspaceId is required');

      const json = await callJson({
        url: `${directusBaseUrl}/items/workspace/${workspaceId}`,
        method: 'PATCH',
        body: payload || {},
        token,
        fallbackError: 'Failed to update workspace'
      });
      return json?.data || null;
    },

    async getWorkspaceBillingStatus(workspaceId) {
      if (!billingBaseUrl) throw new Error('billingBaseUrl is not configured');
      const token = getToken();
      const url = withQuery(`${billingBaseUrl}/billing/workspace-status`, {
        workspace_id: workspaceId || undefined
      });
      return callJson({
        url,
        token,
        fallbackError: 'Failed to load billing status'
      });
    },

    async createBillingPortal({ workspaceId }) {
      if (!billingBaseUrl) throw new Error('billingBaseUrl is not configured');
      const token = getToken();
      return callJson({
        url: `${billingBaseUrl}/billing/portal`,
        method: 'POST',
        body: { workspace_id: workspaceId },
        token,
        fallbackError: 'Failed to create billing portal session'
      });
    },

    async createSubscriptionCheckout({ workspaceId, priceId, successUrl, cancelUrl }) {
      if (!billingBaseUrl) throw new Error('billingBaseUrl is not configured');
      const token = getToken();
      return callJson({
        url: `${billingBaseUrl}/billing/checkout/subscription`,
        method: 'POST',
        body: {
          workspace_id: workspaceId,
          price_id: priceId,
          ...(successUrl ? { success_url: successUrl } : {}),
          ...(cancelUrl ? { cancel_url: cancelUrl } : {})
        },
        token,
        fallbackError: 'Failed to create subscription checkout'
      });
    },

    async claimCheckout({ sessionId } = {}) {
      if (!billingBaseUrl) throw new Error('billingBaseUrl is not configured');
      const token = getToken();
      return callJson({
        url: `${billingBaseUrl}/billing/checkout/claim`,
        method: 'POST',
        body: sessionId ? { session_id: sessionId } : {},
        token,
        fallbackError: 'Failed to link purchase to workspace'
      });
    },

    async createTopupCheckout({ workspaceId, topupPriceId, quantity = 1, successUrl, cancelUrl }) {
      if (!billingBaseUrl) throw new Error('billingBaseUrl is not configured');
      const token = getToken();
      return callJson({
        url: `${billingBaseUrl}/billing/checkout/topup`,
        method: 'POST',
        body: {
          workspace_id: workspaceId,
          topup_price_id: topupPriceId,
          quantity,
          ...(successUrl ? { success_url: successUrl } : {}),
          ...(cancelUrl ? { cancel_url: cancelUrl } : {})
        },
        token,
        fallbackError: 'Failed to create top-up checkout'
      });
    }
  };
}

export { DEFAULT_PROFILE_FIELDS };
